import React, { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../components/Layout";
import SummaryLists from "../summaryreq/Summarylists";
import { addMessage } from "../../store/slices/memberSlice";
import { summary } from "../../services";
import _ from "lodash";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Send, Calendar as CalendarIcon, ExternalLink, ChevronDown } from "lucide-react";
import DateBox from "../../components/DateBox";


export default function Summaryreq() {
  // Keep date-only values stable by mapping selected local day to UTC midnight.
  const toUTCDateOnly = (date) => {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  };

  const formatUTCDateForApi = (date) => {
    const utcDate = toUTCDateOnly(date);
    if (!utcDate) return "";
    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(utcDate.getUTCDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  const getTodayUTCDate = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  };

  const [selectedDate, setSelectedDate] = useState(getTodayUTCDate());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errMsg, setErrMsg] = useState({
    err: false,
    msg: "",
    color: "warning",
  });
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.userinfo);

  const handleSubmit = async () => {
    setErrMsg({ err: false, msg: "", color: "red" });

    if (_.isEmpty(message) || !selectedDate) {
      setErrMsg({ err: true, msg: "Please enter valid Summary", color: "red" });
      return;
    }
    setIsLoading(true);
    try {
      const formattedDate = formatUTCDateForApi(selectedDate);
      const messageData = {
        SummaryDate: formattedDate,
        Summary: message,
      };
      let res = await summary(messageData);
      dispatch(addMessage(messageData));
      setMessage("");
      toast.success(res.data.Message, {
        position: "top-right",
        className: "dark:bg-gray-900 dark:text-white",
      });
    } catch (error) {
      toast.error(error?.response?.data?.Message || "Something went wrong", {
        position: "top-right",
        className: "dark:bg-gray-900 dark:text-white",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isWeekday = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center justify-center bg-transparent dark:bg-transparent px-4 py-8"
      >
        <div className="w-full max-w-2xl bg-white dark:bg-gray-900/50 backdrop-blur-xl border border-gray-200 dark:border-blue-gray-900 p-8 rounded-2xl shadow-2xl shadow-blue-500/5 mb-10">
          {/* Header Inside Box */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="p-3 bg-blue-500/10 rounded-xl">
              <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">
              Summary Request
            </h2>
          </div>

          <div className="space-y-6">
            {/* Date Picker */}
            <div>
              <label className="block text-sm  text-gray-700 dark:text-gray-300 mb-2 ms-1">
                Summary Date
              </label>
              <div className="relative group w-72">
                <button
                  type="button"
                  onClick={() => setShowDatePicker(true)}
                  className="w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 outline-none hover:bg-gray-100 dark:hover:bg-white/10 group shadow-sm dark:shadow-none"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div className="bg-white dark:bg-white/10 px-4 py-2.5 rounded-lg font-medium tracking-wide text-sm flex-1 text-center shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent">
                      {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Select Date"}
                    </div>
                  </div>
                  <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showDatePicker ? 'rotate-180 text-blue-500' : ''}`} />
                  </div>
                </button>

                {showDatePicker && (
                  <DateBox
                    mode="single"
                    initialStartDate={selectedDate}
                    onApply={(date) => {
                      setSelectedDate(toUTCDateOnly(date));
                      setShowDatePicker(false);
                    }}
                    onClose={() => setShowDatePicker(false)}
                  />
                )}
              </div>
            </div>

            {/* Summary Textarea */}
            <div>
              <label className="block text-sm  text-gray-700 dark:text-gray-300 mb-2 ms-1">
                Summary Details
              </label>
              <textarea
                rows="6"
                className="w-full p-4 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none resize-none"
                placeholder="What did you accomplish today?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              {errMsg.err && (
                <motion.p
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-red-500 text-sm mt-2 flex items-center gap-1"
                >
                  <span className="w-1 h-1 bg-red-500 rounded-full" />
                  {errMsg.msg}
                </motion.p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="button"
                className={`w-full relative overflow-hidden group flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-bold text-white transition-all duration-300 ${isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/30 active:scale-[0.98]"
                  }`}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-3">
                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Submitting...</span>
                  </div>
                ) : (
                  <>
                    <span>Submit Summary</span>
                    <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* View All Summary Link */}
        {userProfile.MemberRole === 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full max-w-2xl flex justify-end px-2 sm:px-0"
          >
            <Link
              to="/allsummary"
              className="group flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 dark:hover:text-blue-300 transition-colors bg-blue-50 dark:bg-blue-500/5 px-4 py-2 rounded-full border border-blue-100 dark:border-blue-500/20"
            >
              View All Summary
              <ExternalLink className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </Link>
          </motion.div>
        )}
      </motion.div>

      <div className="w-full max-w-6xl mx-auto px-4 pb-20">
        <SummaryLists />
      </div>
    </Layout>
  );
}
